/**
 * Shared shaping for the brokerage-scoped deal `agent` dropdown.
 *
 * Both deal forms build the roster the same way: take every
 * `broker_agent_access` mapping, keep the ones belonging to one brokerage, and
 * offer each agent's NAME (the deal stores the name as text, so id === label).
 *
 * The scope itself comes from different places depending on who is looking:
 *   - land team  -> the `brokerage_id` they picked in the form (`deal.assign`)
 *   - broker agent -> their OWN brokerage. They never see the Brokerage picker,
 *     so the form has no `brokerage_id` to read; the server derives theirs from
 *     this same mapping table and stamps it on the deal. `ownBrokerageId`
 *     resolves the same answer client-side from the acting user's email.
 */

/**
 * The brokerage a user belongs to, per the broker-access mappings.
 *
 * Returns null for anyone with no mapping — which is every internal land-team
 * user, so a land person with no brokerage picked still gets an empty roster
 * rather than someone else's.
 *
 * @param {Array<object>} rows broker_agent_access rows
 * @param {string} email the acting user's email
 * @returns {number|string|null} their brokerage_id, or null
 */
export const ownBrokerageId = (rows, email) => {
  const wanted = String(email || "").trim().toLowerCase();
  if (!wanted || !Array.isArray(rows)) return null;
  const mine = rows.find(
    (r) => String(r?.email || "").trim().toLowerCase() === wanted
  );
  return mine?.brokerage_id ?? null;
};

/**
 * The `{ id, label }` options for one brokerage's agents.
 *
 * SUSPENDED agents (`active === false`) are dropped — the endpoint returns them
 * so the maintenance screen can offer reactivation, but a suspended agent must
 * not be assignable to a deal. Nameless rows are dropped too: the deal stores
 * the name, so a row without one has nothing to offer.
 *
 * @param {Array<object>} rows broker_agent_access rows
 * @param {number|string|null|undefined} brokerageId the scope; null => []
 * @returns {Array<{id: string, label: string}>}
 */
export const agentRosterOptions = (rows, brokerageId) => {
  if (brokerageId == null || brokerageId === "" || !Array.isArray(rows)) {
    return [];
  }
  return rows
    .filter(
      (r) =>
        String(r?.brokerage_id) === String(brokerageId) &&
        r?.active !== false &&
        r?.name != null &&
        String(r.name).trim() !== ""
    )
    .map((r) => ({ id: r.name, label: String(r.name) }));
};
